import React, { useState, useEffect, useCallback } from 'react';

import useTmsApi from '../../../hooks/useTmsApi';
import getPhoneInventoryStatus from '../../../utils/phoneStatus/getPhoneInventoryStatus';
import { NearbyStore, ReserveStatus, StoreLogo } from '../../../types';
import StoreItem from './StoreItem';
import {
  AdditionalStores,
} from './styles';

interface Props {
  className?: string;
  allStoreLogos: StoreLogo[];
  nearbyStores?: NearbyStore[];
  activeStore?: Record<string, any>;
  onClick?: () => void;
  limit?: number;
  inventoryLabels?: Record<string, any>;
  selectedSKU?: string;
  page?: string;
  selectedCarrier?: string;
  includeTemporaryClosed?: boolean;
  hideInventoryStatus?: boolean;
  reserveStatus?: string;
  isKioskWelcome?: boolean;
}

// TODO TMSP-3552 [2025-05-30] Add missing unit test coverage

export const StoreList = ({
  className = '',
  allStoreLogos,
  nearbyStores,
  activeStore,
  limit = 5,
  onClick,
  selectedSKU,
  selectedCarrier,
  inventoryLabels,
  page,
  includeTemporaryClosed = true,
  hideInventoryStatus = false,
  reserveStatus = ReserveStatus.ChatWithMobilist,
  isKioskWelcome = false,
}: Props) => {
  const isMapList = className.includes('map-list');
  const isReserveList = className.includes('reserve-blade');

  const [payload, setPayload] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [offerWithInventory, setOfferWithInventory] = useState<NearbyStore[]>([]);
  const [tempPayload, setTempPayload] = useState([]);

  // This section will create payload as per BE requirements
  const preparePayload = useCallback(() => {
    if (selectedSKU) {
      nearbyStores?.slice(0, limit).map((store) => {
        // Extracting required digits from loaction code for StoreID
        let StoreId = store?.childLocationIds?.DEPstore || null;
        if (StoreId) {
          StoreId = StoreId.match(/\d+/);
          if (StoreId) {
            // eslint-disable-next-line
            StoreId = StoreId[0];
            StoreId = StoreId.slice(StoreId.length - 5);

            const newPayload = [...tempPayload];
            newPayload.push({
              storeId: StoreId,
              productSku: selectedSKU,
              productType: 'phone',
            });
            if (newPayload.length > 0) {
              setTempPayload(
                newPayload,
              );
            }
          }
        }

        return '';
      });
    }
  }, [limit, nearbyStores, selectedSKU, tempPayload]);

  /* 
    Using API hook to fetch data from backend.
    Whenever payload changes this hook will rerun.
   */
  const { response, error } = useTmsApi(
    '/inventory/product-inventory',
    payload,
  );

  // This section will add the stock count of device in the nearbyStore object
  useEffect(() => {
    const storesWithInventoryStatus = [];
    if (response) {
      const inventoryAPIRes = response.response;
      inventoryAPIRes.map((inventory) => {
        nearbyStores?.slice(0, limit).map((item) => {
          if (item.childLocationIds.DEPstore.includes(inventory.storeId)) {
            storesWithInventoryStatus.push({
              ...item,
              status: inventory.status,
            });
          }
          return '';
        });
        return '';
      });
    }
    if (storesWithInventoryStatus.length) {
      setOfferWithInventory(storesWithInventoryStatus);
    } else if (nearbyStores) {
      setOfferWithInventory(nearbyStores);
    }
  }, [response, nearbyStores, limit]);

  useEffect(() => {
    if (error) {
      setApiError(true);
    }
  }, [error]);

  // Set the payload only when selectedSKU is changed
  useEffect(() => {
    if (selectedSKU && !hideInventoryStatus && tempPayload.length > 0) {
      preparePayload();
      setApiError(false);
      setPayload({ request: tempPayload });
    }
    // TODO [2025-05-30] TMSP-3194 Remove the lint disable and find out why an inifinte loop occurs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSKU]);
  if (!nearbyStores) return null;
  return (
    <AdditionalStores className={className} data-testid="additional-stores">
      {nearbyStores.length === 0 && (
        <li>
          <p>No Results</p>
        </li>
      )}
      {offerWithInventory
        .filter(
          // eslint-disable-next-line max-len
          (store) => (includeTemporaryClosed || store.commonInfo?.businessHours.length > 0) && (store !== null),
        )
        .slice(0, limit)
        .map((store, index) => {
          const inventoryStatus = getPhoneInventoryStatus({
            reserveStatus,
            inventoryStatus: store?.status,
          });
          return (
            <li key={store.commonInfo.lclId}>
              <StoreItem
                store={store}
                activeStore={activeStore}
                isMapList={isMapList}
                isReserveList={isReserveList}
                allStoreLogos={allStoreLogos}
                index={index}
                onClick={onClick}
                inventoryLabels={inventoryLabels}
                inventoryStatus={hideInventoryStatus ? '' : inventoryStatus}
                selectedSKU={selectedSKU}
                selectedCarrier={selectedCarrier}
                page={page}
                apiError={apiError}
                className={className}
                isKioskWelcome={isKioskWelcome}
              />
            </li>
          );
        })}
    </AdditionalStores>
  );
};
